import { useState, useCallback } from "react";
import {
  Form,
  Input,
  Button,
  Descriptions,
  Modal,
  Popconfirm,
  notification,
  Tooltip,
  Space,
} from "antd";
import { DeleteOutlined, LockOutlined, LoginOutlined } from "@ant-design/icons";
import axios from "axios";
import { useHistory } from "react-router-dom";

import { useAuth } from "../../hooks/useAuth";
import useSegment from "../../hooks/useSegment";
import { handleError, sleep } from "../../helpers";
import { SERVICE_LEVEL } from "../../constants";
import tokens from "../../styles/tokens";

/* ============================================================================
 * PRESERVED:upgrade-cluster — disabled in feature/brand-refresh-foundation.
 *
 * Last working implementation: master tip 0e55014. The backend route
 * POST /clusters/:id/upgrade is still served by functions/src/api.ts, so the
 * client side is the only thing that needs to come back.
 *
 * Restoration steps:
 *   1. Re-add IMPORTS below to the import block above.
 *   2. Re-add STATE/EFFECTS inside the component (after `setResetting` state).
 *   3. Re-add LOGIC after `handleLoginClick`, before `isDedicated`.
 *   4. Re-add JSX inside `cluster-detail-page__panel`, between the
 *      `__panel-header` div and the <Descriptions> table.
 *      Look for the `{/ * PRESERVED:upgrade-cluster — Alert rendered here * /}`
 *      breadcrumb in the JSX tree.
 *   5. Adapt to the new design system before re-enabling:
 *        - Swap hardcoded `#ff4d4f` / `#ffffffcc` for `tokens.semantic.danger`
 *          and `tokens.surface[200]` (or equivalents).
 *        - Restyle action <Button>s with `className="kinetica-gradient-btn"`
 *          to match the panel's button language.
 *        - The Alert previously sat as a sibling of the old <h2>; in the new
 *          panel layout it should live between `__panel-header` and the
 *          <Descriptions> table.
 *
 * Reference (full original file, not just this slice):
 *   git show 0e55014:client/src/components/Cluster/SummaryPane.js
 * Tracking ticket: KUI-XXXX (TODO: replace with real Jira ID).
 *
 * ── IMPORTS ─────────────────────────────────────────────────────────────────
 *
 *   import { useMemo, useEffect } from "react";       // add useMemo, restore useEffect
 *   import { Alert, Select, Tag } from "antd";
 *   import { UpCircleOutlined } from "@ant-design/icons";
 *   import { format } from "date-fns";
 *   import firebase from "../../services/firebase";
 *
 *   const { Option } = Select;
 *
 * ── STATE / EFFECTS (inside SummaryPane, after setResetting) ────────────────
 *
 *   const db = firebase.firestore();
 *   const [clusterActions, setClusterActions] = useState([]);
 *   const [selectedUpgradeVersion, setSelectedUpgradeVersion] = useState(null);
 *
 *   const getActions = useCallback(() => {
 *     const fetch = async () => {
 *       try {
 *         const token = await auth.getIdToken();
 *         const resp = await axios.get(
 *           `${process.env.REACT_APP_FB_FUNCTIONS_DOMAIN}/actions/cluster/${cluster.id}`,
 *           { headers: { Authorization: `Bearer ${token}` } }
 *         );
 *         const actions = resp?.data?.actions
 *           .filter((action) => Object.keys(action).length > 1)
 *           .sort((a, b) => b.created._seconds - a.created._seconds);
 *         setClusterActions(actions);
 *       } catch (error) {
 *         handleError(error);
 *         setClusterActions([]);
 *       }
 *     };
 *     fetch();
 *   }, [auth, cluster.id]);
 *
 *   useEffect(() => {
 *     const unsubscribe = db
 *       .collection("dashboards")
 *       .doc(auth.user.db.id)
 *       .onSnapshot(() => getActions());
 *     return () => unsubscribe();
 *   }, [db, auth, getActions]);
 *
 * ── LOGIC (after handleLoginClick) ──────────────────────────────────────────
 *
 *   const handleUpgrade = async () => {
 *     try {
 *       const version = cluster.upgrade_info.upgradeVersions.find(
 *         (v) => v.tag === selectedUpgradeVersion
 *       );
 *       const token = await auth.getIdToken();
 *       await axios.post(
 *         `${process.env.REACT_APP_FB_FUNCTIONS_DOMAIN}/clusters/${cluster.id}/upgrade`,
 *         { version },
 *         { headers: { Authorization: `Bearer ${token}` } }
 *       );
 *       segment.track("database_upgrade");
 *       notification.success({
 *         message: "Database Upgrade",
 *         description: "Database is being upgraded.",
 *       });
 *     } catch (error) {
 *       handleError(error);
 *     }
 *   };
 *
 *   const upgradeInfo = useMemo(() => {
 *     if (cluster.upgrade_info) return cluster.upgrade_info;
 *     return null;
 *   }, [cluster]);
 *
 *   useEffect(() => {
 *     if (
 *       upgradeInfo &&
 *       upgradeInfo.upgradeAvailable &&
 *       upgradeInfo.upgradeVersions.length > 0
 *     ) {
 *       setSelectedUpgradeVersion(
 *         upgradeInfo.upgradeVersions[upgradeInfo.upgradeVersions.length - 1].tag
 *       );
 *     }
 *   }, [upgradeInfo]);
 *
 *   const isUpgrading = useMemo(
 *     () => clusterActions.some(
 *       (a) => a.type === "upgrade_cluster" && a.status === "pending"
 *     ),
 *     [clusterActions]
 *   );
 *
 *   const lastUpgrade = useMemo(
 *     () => clusterActions.find((a) => a.type === "upgrade_cluster"),
 *     [clusterActions]
 *   );
 *
 *   const handleUpgradeVersionChange = (v) => setSelectedUpgradeVersion(v);
 *
 * ── JSX (place between __panel-header div and <Descriptions>) ───────────────
 *
 *   {upgradeInfo &&
 *     upgradeInfo.upgradeAvailable &&
 *     upgradeInfo.upgradeVersions.length > 0 &&
 *     cluster.service_level !== SERVICE_LEVEL.FREE_SHARED && (
 *       <Alert
 *         message="Upgrade Available"
 *         description={
 *           !isUpgrading && (
 *             <>
 *               Please select an upgrade version
 *               <Select
 *                 value={selectedUpgradeVersion}
 *                 onChange={handleUpgradeVersionChange}
 *                 disabled={isUpgrading}
 *                 style={{ minWidth: 180, marginLeft: 10 }}
 *               >
 *                 {upgradeInfo.upgradeVersions.map((version) => (
 *                   <Option key={version.semVer} value={version.tag}>
 *                     <Tag style={{ color: "#999999" }}>Tag: {version.tag}</Tag>
 *                   </Option>
 *                 ))}
 *               </Select>
 *               {lastUpgrade && (
 *                 <div
 *                   style={{
 *                     fontSize: "14px",
 *                     backgroundColor:
 *                       lastUpgrade.status === "error" ? "#ff4d4f22" : "#ffffffcc",
 *                     padding: "6px 15px",
 *                     marginTop: "20px",
 *                     display: "block",
 *                     borderRadius: 5,
 *                     border:
 *                       lastUpgrade.status === "error"
 *                         ? "1px solid #ff4d4f33"
 *                         : "1px solid #ffffffcc",
 *                   }}
 *                 >
 *                   <i>
 *                     {`Last Attempt: ${format(
 *                       new Date(lastUpgrade.created._seconds * 1000),
 *                       "yyyy-MM-dd hh:mm aaa"
 *                     )}`}
 *                     <br />
 *                     {`Status: ${lastUpgrade.log} (${lastUpgrade.status})`}
 *                   </i>
 *                 </div>
 *               )}
 *             </>
 *           )
 *         }
 *         type="info"
 *         action={
 *           isUpgrading ? (
 *             <Button type="primary" icon={<UpCircleOutlined />} loading>
 *               Upgrading
 *             </Button>
 *           ) : selectedUpgradeVersion ? (
 *             <Popconfirm
 *               key="upgrade"
 *               title="Database will be unavailable while performing an upgrade. Would you like to continue?"
 *               onConfirm={handleUpgrade}
 *               okText="Yes"
 *               cancelText="No"
 *               placement="topRight"
 *             >
 *               <Button type="primary" icon={<UpCircleOutlined />}>
 *                 Upgrade
 *               </Button>
 *             </Popconfirm>
 *           ) : (
 *             <Button type="primary" icon={<UpCircleOutlined />} disabled>
 *               Upgrading
 *             </Button>
 *           )
 *         }
 *         style={{ marginBottom: 20 }}
 *         showIcon
 *       />
 *     )}
 * ============================================================================
 */

const SummaryPane = ({ cluster, assignment }) => {
  const auth = useAuth();
  const segment = useSegment(auth);
  const [form] = Form.useForm();
  const history = useHistory();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resetting, setResetting] = useState(false);

  const resetPassword = useCallback(() => {
    const fetch = async () => {
      try {
        const token = await auth.getIdToken();
        await axios.post(
          `${process.env.REACT_APP_FB_FUNCTIONS_DOMAIN}/assignments/${assignment.id}/resetpassword`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        notification.success({
          message: "Password Reset",
          description:
            "Your database password reset has been requested. Please check your e-mail in a few minutes for your new password.",
        });
      } catch (error) {
        handleError(error);
      } finally {
        setResetting(false);
      }
    };
    fetch();
  }, [assignment, auth]);

  const handleResetPasswordClick = () => {
    segment.track("reset_database_password");
    setResetting(true);
    resetPassword();
  };

  const onFinish = async (values) => {
    if (
      !values.confirm_cluster_name ||
      values.confirm_cluster_name !== cluster.cluster_name
    ) {
      form.setFields([
        {
          name: "confirm_cluster_name",
          value: values.confirm_cluster_name,
          errors: ["Database name must match exactly!"],
        },
      ]);
      return;
    }

    try {
      setDeleting(true);
      await sleep(1000);

      const token = await auth.getIdToken();
      await axios.delete(
        `${process.env.REACT_APP_FB_FUNCTIONS_DOMAIN}/clusters/${cluster.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      segment.track("database_delete");

      notification.success({
        message: "Database delete",
        description: "Your database is being deprovisioned.",
      });
      setDeleteOpen(false);
      form.resetFields();

      history.push("/dashboard");
    } catch (error) {
      handleError(error);
    } finally {
      setDeleting(false);
    }
  };

  const handleDelete = () => {
    form.submit();
  };

  const handleCancel = () => {
    setDeleteOpen(false);
    form.resetFields();
  };

  const handleLoginClick = (cluster, username) => () => {
    segment.track("workbench_login");
    window.open(
      `${cluster.workbench_url}/workbench/login?username=${username}`
    );
  };

  const isDedicated = cluster.service_level === SERVICE_LEVEL.DEDICATED;
  const canLogin = assignment?.status === "complete";

  return (
    <>
      <div className="cluster-detail-page__panel">
        <div className="cluster-detail-page__panel-header">
          <h2 className="cluster-detail-page__panel-title">Database Info</h2>
          <Space size={8}>
            <Popconfirm
              key="reset"
              title={
                <>
                  Your new auto-generated password will be e-mailed to you.
                  <br />
                  Are you sure you want to reset your database password?
                </>
              }
              onConfirm={handleResetPasswordClick}
              okText="Yes"
              cancelText="No"
              disabled={resetting}
            >
              <Button>
                <LockOutlined /> Reset Password
              </Button>
            </Popconfirm>
            {isDedicated && (
              <Button
                icon={<DeleteOutlined />}
                onClick={() => setDeleteOpen(true)}
                danger
              >
                Delete
              </Button>
            )}
            <Tooltip
              title={
                <div style={{ padding: "5px 10px" }}>
                  DB User: <strong>{assignment?.admin_username}</strong>
                </div>
              }
              color="magenta"
            >
              <Button
                type="primary"
                onClick={handleLoginClick(cluster, assignment?.admin_username)}
                disabled={!canLogin}
                className="kinetica-gradient-btn"
              >
                <LoginOutlined /> Login
              </Button>
            </Tooltip>
          </Space>
        </div>

        {/* PRESERVED:upgrade-cluster — Alert rendered here pre-brand-refresh.
            See the PRESERVED:upgrade-cluster block at the top of this file. */}

        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="Hostname">
            {cluster?.hostname}
          </Descriptions.Item>
          {isDedicated && cluster.current_version && (
            <Descriptions.Item label="Current Version">
              {cluster?.current_version}
            </Descriptions.Item>
          )}
          {isDedicated && (
            <>
              <Descriptions.Item label="Size / Type">
                {cluster?.cluster_size} / {cluster?.cluster_type}
              </Descriptions.Item>
              <Descriptions.Item label="Provider">
                {cluster?.cloud_provider}
              </Descriptions.Item>
            </>
          )}
          <Descriptions.Item label="Workbench">
            <a href={cluster?.workbench_url} target="_blank" rel="noreferrer">
              {cluster?.workbench_url}
            </a>
          </Descriptions.Item>
          {isDedicated && (
            <Descriptions.Item label="GAdmin">
              <a
                href={`${cluster?.workbench_url}/gadmin`}
                target="_blank"
                rel="noreferrer"
              >
                {cluster?.workbench_url}/gadmin
              </a>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="API Endpoint">
            <a
              href={`${cluster?.workbench_url}/${cluster?.cluster_name}/gpudb-0`}
              target="_blank"
              rel="noreferrer"
            >
              {cluster?.workbench_url}/{cluster?.cluster_name}/gpudb-0
            </a>
          </Descriptions.Item>
          <Descriptions.Item label="Postgres">
            {cluster?.workbench_url.replace("https://", "")}:{cluster?.postgres}
          </Descriptions.Item>
        </Descriptions>
      </div>

      {deleteOpen && (
        <Modal
          title={<span style={{ color: tokens.semantic.danger }}>Delete Database</span>}
          visible={deleteOpen}
          okText="Confirm"
          okButtonProps={{ danger: true }}
          onOk={handleDelete}
          confirmLoading={deleting}
          onCancel={handleCancel}
          destroyOnClose
          centered
        >
          <p>
            This is irreversible. We will delete your database and all
            associated backups. All database data will be purged and
            irretrievable.
          </p>
          <p>
            Confirm by entering the name of your cluster below:
            <br />
            <strong style={{ color: tokens.semantic.danger }}>{cluster.cluster_name}</strong>
          </p>
          <Form
            form={form}
            layout="vertical"
            initialValues={{}}
            onFinish={onFinish}
          >
            <Form.Item label="Confirm cluster name" name="confirm_cluster_name">
              <Input placeholder="My cluster name" size="large" />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default SummaryPane;
